import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, Play, Award, Filter, Send } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import { useSubmissionsOpen } from "../hooks/useSubmissionsOpen";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const genres = ["All", "Drama", "Sci-Fi", "Documentary", "Animation", "Experimental", "Horror", "Comedy", "Music Video", "Thriller", "Action", "Western", "Adventure"];

export default function Films() {
  const submissionsOpen = useSubmissionsOpen();
  const [films, setFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    genre: "All",
    search: "",
  });

  useEffect(() => {
    fetchFilms();
  }, [filters]);

  const fetchFilms = async () => {
    try {
      let url = `${API}/films?`;
      if (filters.genre !== "All") url += `genre=${filters.genre}&`;
      if (filters.search) url += `director=${filters.search}&`;
      
      const response = await axios.get(url);
      setFilms(response.data);
    } catch (error) {
      console.error("Error fetching films:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="Films" 
        description="Explore our curated collection of AI-generated films. Discover groundbreaking AI cinema from talented filmmakers around the world."
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-purple-light mb-4 font-mono">
            Showcase
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-syne text-4xl md:text-6xl font-bold text-white mb-4">
                AI Films & Winners
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl">
                Explore our curated collection of AI-generated films from talented 
                filmmakers around the world.
              </p>
            </div>
            {submissionsOpen && (
              <Link to="/submit" data-testid="submit-film-btn">
                <Button className="bg-purple hover:bg-purple-hover text-white rounded-full px-6 whitespace-nowrap">
                  <Send className="w-4 h-4 mr-2" />
                  Submit Your Film
                </Button>
              </Link>
            )}
          </div>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-12 p-4 bg-void-surface border border-white/5">
          <Filter className="w-5 h-5 text-slate-400" />
          
          <Select 
            value={filters.genre} 
            onValueChange={(value) => setFilters({...filters, genre: value})}
          >
            <SelectTrigger 
              className="w-40 bg-void border-white/10 text-white"
              data-testid="genre-filter"
            >
              <SelectValue placeholder="Genre" />
            </SelectTrigger>
            <SelectContent className="bg-void-surface border-white/10">
              {genres.map((genre) => (
                <SelectItem key={genre} value={genre} className="text-white hover:bg-white/10">
                  {genre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <input
            type="text"
            placeholder="Search..."
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
            data-testid="film-search"
            className="px-4 py-2 bg-void border border-white/10 text-white placeholder-slate-500 focus:border-purple/50 outline-none transition-colors"
          />

          {(filters.genre !== "All" || filters.search) && (
            <button
              onClick={() => setFilters({ genre: "All", search: "" })}
              data-testid="clear-filters"
              className="text-sm text-purple-light hover:text-white transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Films Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple animate-spin" />
          </div>
        ) : films.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-400">No films found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {films.map((film, index) => (
              <motion.div
                key={film.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group"
              >
                <Link to={`/films/${film.id}`} data-testid={`film-${index}`}>
                  <div className="aspect-video mb-4 overflow-hidden bg-void-surface relative">
                    <img 
                      src={film.poster_url || "https://images.unsplash.com/photo-1696479670605-aeeb206e1fcb?crop=entropy&cs=srgb&fm=jpg&q=85"}
                      alt={film.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 rounded-full bg-purple/90 flex items-center justify-center">
                        <Play className="w-6 h-6 text-white ml-1" />
                      </div>
                    </div>
                    {film.award && (
                      <span className="absolute top-2 right-2 px-2 py-1 text-xs bg-yellow-500/20 text-yellow-500 flex items-center gap-1">
                        <Award className="w-3 h-3" />
                        {film.award}
                      </span>
                    )}
                  </div>
                  <h3 className="font-syne font-bold text-white group-hover:text-purple-light transition-colors">
                    {film.title}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {film.director} • {film.year}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
